import React, { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Edit, Loader2, Plus, Power, Search } from "lucide-react";
import { SUPPLIER_TYPES, type SupplierRecord } from "@/types/inventory";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";
const blank = { name: "", supplier_type: "YARN", contact_person: "", phone: "", email: "", gstin: "", address: "", city: "", state: "", payment_terms: "", notes: "" };

export function SuppliersPage() {
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("supplier.manage");
  const [search, setSearch] = useUrlSearch();
  const [type, setType] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [dialog, setDialog] = useState<SupplierRecord | "new" | null>(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const list = usePagedList<SupplierRecord>({
    deps: [search, type, showClosed],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "50" });
      if (search.trim()) qs.set("search", search.trim());
      if (type) qs.set("type", type);
      if (showClosed) qs.set("include_closed", "true");
      try {
        return unwrapPage<SupplierRecord>(await api.get(`/api/Supplier/List?${qs}`), 50);
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load suppliers");
        return { rows: [], total: 0, page, page_size: 50 };
      }
    },
  });

  useEffect(() => {
    if (!dialog) return;
    if (dialog === "new") return setForm(blank);
    setForm({
      name: dialog.name, supplier_type: dialog.supplier_type, contact_person: dialog.contact_person || "", phone: dialog.phone || "", email: dialog.email || "", gstin: dialog.gstin || "",
      address: dialog.address || "", city: dialog.city || "", state: dialog.state || "", payment_terms: dialog.payment_terms || "", notes: dialog.notes || "",
    });
  }, [dialog]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return error("Validation Error", "Enter the supplier's name");
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() || undefined : v]));
      if (dialog && dialog !== "new") await api.put(`/api/Supplier/Update/${dialog.id}`, body);
      else await api.post("/api/Supplier/Insert", body);
      success("Supplier saved", "");
      setDialog(null);
      list.reload();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (s: SupplierRecord) => {
    try {
      await api.patch(`/api/Supplier/SetActive/${s.id}`, { active: !s.status });
      list.reload();
    } catch (err: any) {
      error("Could not change", err.message || "Please try again");
    }
  };

  const set = (k: keyof typeof blank) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Suppliers</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Mills, yarn and chemical suppliers, and job workers you receive material from</p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setDialog("new")} className="h-9 gap-1.5 shadow-sm">
            <Plus className="size-4" />
            Add supplier
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Search by name, contact, phone or GSTIN…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
        </div>
        <select className="h-9 rounded-lg border border-input bg-background px-3 text-sm" value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
          <option value="">All types</option>
          {Object.entries(SUPPLIER_TYPES).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <label className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show closed
        </label>
      </div>

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={5} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Supplier</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>GSTIN / terms</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((s) => (
                <TableRow key={s.id} className={s.status ? "" : "opacity-60"}>
                  <TableCell>
                    <div className="font-semibold text-sm">{s.name} {!s.status && <Badge variant="outline">Closed</Badge>}</div>
                    <div className="text-[11px] text-muted-foreground"><code className="font-mono">{s.code}</code>{s.city ? ` · ${s.city}` : ""}</div>
                  </TableCell>
                  <TableCell className="text-sm">{SUPPLIER_TYPES[s.supplier_type] || s.supplier_type}</TableCell>
                  <TableCell className="text-sm">
                    {s.contact_person || "-"}
                    <div className="text-[11px] text-muted-foreground">{[s.phone, s.email].filter(Boolean).join(" · ")}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {s.gstin || "-"}
                    {s.payment_terms && <div className="text-[11px] text-muted-foreground">{s.payment_terms}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="size-8" title="Edit" onClick={() => setDialog(s)}>
                          <Edit className="size-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="size-8" title={s.status ? "Close" : "Reopen"} onClick={() => setActive(s)}>
                          <Power className="size-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">
                    No suppliers yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && !saving && setDialog(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialog && dialog !== "new" ? `Edit ${dialog.name}` : "Add supplier"}</DialogTitle>
            <DialogDescription>Who you buy material from, or send it to for processing.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="flex flex-col min-h-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2 text-sm overflow-y-auto max-h-[64vh] pr-1">
              <div className="space-y-1.5 sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={set("name")} className="h-10" /></div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <select className={field} value={form.supplier_type} onChange={(e) => setForm({ ...form, supplier_type: e.target.value })}>
                  {Object.entries(SUPPLIER_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5"><Label>Contact person</Label><Input value={form.contact_person} onChange={set("contact_person")} className="h-10" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="h-10" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="h-10" /></div>
              <div className="space-y-1.5"><Label>GSTIN</Label><Input value={form.gstin} onChange={set("gstin")} className="h-10" /></div>
              <div className="space-y-1.5"><Label>Payment terms</Label><Input value={form.payment_terms} onChange={set("payment_terms")} placeholder="e.g. 30 days" className="h-10" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={set("address")} className="h-10" /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={set("city")} className="h-10" /></div>
              <div className="space-y-1.5"><Label>State</Label><Input value={form.state} onChange={set("state")} className="h-10" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} /></div>
            </div>
            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setDialog(null)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="gap-1.5">
                {saving && <Loader2 className="size-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
